import axios from 'axios';

export const getApiBase = () => {
  const host = window.location.hostname;
  if (host === 'localhost') return 'http://localhost:5000';
  const tenantSub = localStorage.getItem('quindi_tenant') || 'sosfire';
  const domain = host.split('.').slice(-2).join('.');
  return `https://${tenantSub}.${domain}`;
};

const api = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

// Imposta baseURL dinamicamente ad ogni richiesta
api.interceptors.request.use(async config => {
  if (!config.baseURL && !config.url?.startsWith('http')) {
    config.url = getApiBase() + '/api' + config.url;
  }
  return config;
}, err => Promise.reject(err));

// Controlla scadenza JWT prima di ogni richiesta
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + 30000; // 30sec buffer
  } catch { return true; }
};

const refreshToken = async () => {
  const user = JSON.parse(localStorage.getItem('quindi_user') || '{}');
  const pass = localStorage.getItem('quindi_pass');
  if (!user.username || !pass) throw new Error('No credentials');
  const base = getApiBase();
  const res  = await axios.post(`${base}/api/auth/login`, { username: user.username, password: pass });
  const newToken = res.data.token;
  localStorage.setItem('quindi_token', newToken);
  return newToken;
};

api.interceptors.request.use(async config => {
  let token = localStorage.getItem('quindi_token');
  if (token && isTokenExpired(token)) {
    try { token = await refreshToken(); } catch { 
      localStorage.clear(); 
      window.location.href = '/login'; 
      return config; 
    }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh token on 401
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const tenant   = localStorage.getItem('quindi_tenant') || 'sosfire';
      const user     = JSON.parse(localStorage.getItem('quindi_user') || '{}');
      const password = localStorage.getItem('quindi_pass');

      if (!password || !user.username) {
        localStorage.removeItem('quindi_token');
        localStorage.removeItem('quindi_user');
        window.location.href = '/login';
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      isRefreshing = true;
      try {
        const base = getApiBase();
        // Il tenant è nell'Host header — basta mandare username+password
        const res  = await axios.post(`${base}/api/auth/login`, {
          username: user.username,
          password,
        });
        const newToken = res.data.token;
        localStorage.setItem('quindi_token', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        localStorage.removeItem('quindi_token');
        localStorage.removeItem('quindi_user');
        localStorage.removeItem('quindi_pass');
        window.location.href = '/login';
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login: async (username, password) => {
    // Usa getApiBase() al momento della chiamata, non alla creazione
    const base = getApiBase();
    const res  = await axios.post(`${base}/api/auth/login`, { username, password });
    // Aggiorna il token in axios dopo il login
    if (res.data?.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    }
    return res;
  },
  me: () => api.get('/auth/me'),
};

export const clientiApi = {
  getAll:  ()         => api.get('/clienti'),
  getById: id         => api.get(`/clienti/${id}`),
  create:  data       => api.post('/clienti', data),
  update:  (id, data) => api.put(`/clienti/${id}`, data),
  delete:  id         => api.delete(`/clienti/${id}`),
};

export const spostaApi = {
  preview: (id, nuovaData, cascata) =>
    api.put(`/interventi/${id}/sposta`, { nuovaData, cascata, anteprima: true }),
  conferma: (id, nuovaData, cascata) =>
    api.put(`/interventi/${id}/sposta`, { nuovaData, cascata, anteprima: false }),
};

export const reportApi = {
  interventi: (anno, mese) =>
    api.get(`/report/interventi?anno=${anno}&mese=${mese}`, { responseType: 'blob' }),
};

export const interventiApi = {
  getAll:  ()         => api.get('/interventi'),
  getById: id         => api.get(`/interventi/${id}`),
  create:  data       => api.post('/interventi', data),
  update:  (id, data) => api.put(`/interventi/${id}`, data),
};

export const ficApi = {
  status:     ()             => api.get('/fic/status'),
  clienti:    ()             => api.get('/fic/clienti'),
  fatture:    (page = 1)     => api.get(`/fic/fatture?page=${page}`),
  preventivi: (page = 1)     => api.get(`/fic/preventivi?page=${page}`),
  ddt:        (page = 1)     => api.get(`/fic/ddt?page=${page}`),
  magazzino:  (page=1, q='') => api.get(`/fic/magazzino?page=${page}&q=${encodeURIComponent(q)}`),
};

export default api;
